import React, { memo, useMemo } from 'react';
import './systemComponentCard.css';


export type InventoryStatus = 'DETECTED' | 'NO_DATA' | 'CHECK';

interface IInventoryParameter {
  key: string;
  visibleName: string;
  value: string | null;
}

interface IInventoryComponent {
  id: string;
  label: string;
  parameters: IInventoryParameter[];
}

interface SystemComponentCardProps {
  item: IInventoryComponent
}

const isEmptyValue = (value: string | null | undefined): boolean =>
  value === null || value === undefined || value.trim() === '' || value.trim() === '-' || value.trim().toLowerCase() === 'na';

const isInvalidValue = (value: string | null | undefined): boolean => {
  const normalized = value?.trim().toLowerCase();
  return normalized === 'invalid' || normalized === 'check';
};


export const getComponentStatus = (params: IInventoryParameter[]): InventoryStatus => {
  if (params.some((p) => isInvalidValue(p.value))) return 'CHECK';
  if (params.some((p) => !isEmptyValue(p.value))) return 'DETECTED';
  return 'NO_DATA';
};

const STATUS_LABEL: Record<InventoryStatus, string> = {
  DETECTED: 'DETECTED',
  NO_DATA: 'NO DATA',
  CHECK: 'CHECK',
};

function getIconClassForComponent(componentKey): string  {
  const systemComponentIconMap = {
    'ip$rfidCardReader': 'inventory_parameter_rfid_card_icon',
    'ip$inputAcem': 'inventory_parameter_thunderbolt_icon',
    'ip$fanCard': 'inventory_parameter_fan_card_icon',
    'ip$ledCard': 'inventory_parameter_led_card_icon',
    'ip$ledCard2': 'inventory_parameter_led_card_icon',
    'ip$gmd': 'inventory_parameter_gmd_icon',
    'ip$acSenseCard': 'inventory_parameter_thunderbolt_icon',
    'ip$dcsi': 'inventory_parameter_thunderbolt_icon',
    'ip$iocard': 'inventory_parameter_io_card_icon',
    'ip$upper': 'inventory_parameter_upper_icon',
    'ip$evm': 'inventory_parameter_thunderbolt_icon'
  }
  return systemComponentIconMap?.[componentKey] ?? 'inventory_parameter_thunderbolt_icon';
}

const SystemComponentCard = memo(function SystemComponentCard({ item }: SystemComponentCardProps) {
  const params = useMemo<IInventoryParameter[]>(() => item?.parameters ?? [], [item?.parameters]);
  const status = useMemo(() => getComponentStatus(params), [params]);

  const iconClassName = getIconClassForComponent(item?.id);

  return (
    <div className={`inventory__system__card ${status === 'CHECK' ? 'inventory__system__card__border__yellow' : ''}`} id={`inventory-system-card-${item?.id}`}>
      <div className='inventory__system__card__header'>
        <div className='inventory__system__card__title__wrap'>
          <span
            className={
              iconClassName
                ? `rms__icon inventory__system__card__icon ${iconClassName}`
                : 'inventory__system__card__icon inventory__system__card__icon--default'
            }
          />
          <span className='inventory__system__card__title' title={item?.label}>
            {item?.label}
          </span>
        </div>
        <div className={`inventory__system__card__badge inventory__system__card__badge--${status.toLowerCase()}`}>
          <span className='inventory__system__card__badge__marker' />
          {STATUS_LABEL[status]}
        </div>
      </div>

      <div className='inventory__system__card__params'>
        {params.length === 0 ? (
          <div className='inventory__system__card__empty'>No parameters</div>
        ) : (
          params.map((param) => {
            const empty = isEmptyValue(param.value);
            const invalid = isInvalidValue(param.value);
            return (
              <div className='inventory__system__card__param__row' key={param.key}>
                <span className='inventory__system__card__param__label'>{param.visibleName}</span>
                <span
                  className={
                    'inventory__system__card__param__value' +
                    (invalid ? ' inventory__system__card__param__value--invalid' : '') +
                    (empty ? ' inventory__system__card__param__value--empty' : '')
                  }
                >
                  {empty ? '—' : param.value}
                </span>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
});

export default SystemComponentCard;
